"use client";

import { useEffect, useId, useRef, useState } from "react";

import PortableText from "@/components/shared/portable-text";
import { ResponsiveImage } from "@/components/shared/ResponsiveImage";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/ui/container";
import { icon } from "@/components/ui/icons";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ModuleTabbedHero } from "@/lib/types";
import { cn } from "@/lib/ui/cn";

import { Indicators } from "./TabIndicators.client";

type TabbedHeroModuleProps = {
  module: ModuleTabbedHero;
};

const ITEM_ROTATION_INTERVAL = 5000;

const ArrowForwardIcon = icon("ArrowForward");

export function TabbedHeroModule({ module }: TabbedHeroModuleProps) {
  const headerId = useId();
  const [selectedTab, setSelectedTab] = useState(module.tabs[0]?._key || "");
  const [activeItemIdx, setActiveItemIdx] = useState<number>(0);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const interactionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const selectedTabData = module.tabs.find((tab) => tab._key === selectedTab);

  const pauseAutoRotation = () => {
    setIsUserInteracting(true);

    // Clear existing timeout
    if (interactionTimeoutRef.current) {
      clearTimeout(interactionTimeoutRef.current);
    }

    // Resume auto-rotation after 5 seconds of inactivity
    interactionTimeoutRef.current = setTimeout(() => {
      setIsUserInteracting(false);
    }, 5000);
  };

  const handleItemClick = (idx: number) => {
    setActiveItemIdx(idx);
    pauseAutoRotation();
  };

  useEffect(() => {
    if (isUserInteracting) return;

    const interval = setInterval(() => {
      setActiveItemIdx((prevIdx) => {
        const currentTab = module.tabs.find((tab) => tab._key === selectedTab);
        if (!currentTab || !currentTab.items) return 0;
        return (prevIdx + 1) % currentTab.items.length;
      });
    }, ITEM_ROTATION_INTERVAL);

    return () => clearInterval(interval);
  }, [selectedTab, module.tabs, isUserInteracting]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
      }
    };
  }, []);

  const onValueChange = (value: string) => {
    setSelectedTab(value);
    setActiveItemIdx(0);
  };

  return (
    <Container className="max-w-max-width-lg py-padding-section-lg relative flex flex-col">
      {selectedTabData?.media && (
        <div className="absolute inset-0 h-full w-full overflow-hidden opacity-40 blur-3xl">
          <ResponsiveImage src={selectedTabData.media} fill className="object-cover" />
          <div className="absolute z-10 h-full w-full bg-[linear-gradient(180deg,var(--color-indigo-darkest)_0%,rgba(0,0,0,0)_100%)]" />
        </div>
      )}
      <Tabs value={selectedTab} onValueChange={onValueChange} className="relative z-20 w-full">
        <div className="gap-gap-element-xl pb-padding-element-lg desktop:pb-padding-section-sm flex flex-col">
          {module.eyebrow && (
            <Badge variant="success" className="w-fit">
              {module.eyebrow}
            </Badge>
          )}
          <div className="gap-gap-element-md flex items-center justify-between" id={headerId}>
            <TabsList className="shrink-0">
              {module.tabs.map((tab) => (
                <TabsTrigger key={tab._key} value={tab._key}>
                  {tab.triggerTitle}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </div>
        {module.tabs.map((tab) => {
          return (
            <TabsContent
              key={tab._key}
              value={tab._key}
              className="gap-gap-element-xl desktop:flex-row desktop:items-center fade-in animate-in flex flex-col"
            >
              <div className="desktop:aspect-square desktop:flex-1 relative aspect-2/1 w-full">
                {tab.media && (
                  <ResponsiveImage
                    src={tab.media}
                    fill
                    className="rounded-lg object-cover"
                    imageOptions={{
                      mobileWidth: 600,
                      tabletWidth: 1000,
                      desktopWidth: 600,
                    }}
                  />
                )}
              </div>
              <div className="gap-gap-element-xl tablet:px-padding-global desktop:flex-1 flex flex-col">
                <div className="gap-gap-element-md flex flex-col">
                  <div className="typography-heading-3">{tab.title}</div>
                  <div className="typography-text-large-normal">{tab.body}</div>
                </div>
                <Separator className="desktop:hidden bg-white/15" />
                <ul className="gap-gap-element-lg desktop:list-none desktop:px-padding-element-md flex list-outside list-disc flex-col pl-4">
                  {tab.items.map((item, idx) => {
                    const isActive = activeItemIdx === idx;
                    return (
                      <li
                        key={item._key}
                        onClick={() => handleItemClick(idx)}
                        onMouseEnter={() => setActiveItemIdx(idx)}
                        className={cn(
                          "desktop:flex desktop:flex-row desktop:items-start desktop:relative desktop:before:absolute desktop:before:inset-0 desktop:before:rounded-md desktop:before:backdrop-blur-lg desktop:before:transition-opacity desktop:before:opacity-0 cursor-pointer transition-all",
                          "desktop:before:bg-[linear-gradient(180deg,rgba(189,212,252,0.20)_0%,rgba(178,178,179,0.20)_100%)]",
                          isActive &&
                            "desktop:gap-2.5 desktop:px-padding-element-sm desktop:py-padding-element-lg desktop:before:opacity-100",
                        )}
                      >
                        <Button
                          iconOnly
                          size="sm"
                          variant="tertiary"
                          className={cn(
                            "desktop:block relative z-10 hidden max-w-0 opacity-0 transition-all",
                            isActive && "max-w-8 opacity-100",
                          )}
                        >
                          <ArrowForwardIcon />
                        </Button>
                        <PortableText
                          className="typography-text-medium-normal relative z-10 [&>p]:inline"
                          value={item.body}
                          as="span"
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>
            </TabsContent>
          );
        })}
        <Indicators headerId={headerId} tabs={module.tabs} />
      </Tabs>
    </Container>
  );
}
